import { Injectable, Optional } from '@angular/core';
import { IFrontendSettings } from '../ngrx.stores/frontend.settings/states';
import { ModalService } from '../../../common/services/modal/modal.service';
import { SsrDetailPreviewComponent } from '../components/ssr.detail.preview/ssr.detail.preview.component';
import { environment } from '../../../../environments/environment';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, take, takeUntil } from 'rxjs';

type SsrDetailItemType = 'event' | 'place';

interface SsrDetailEntityLike {
  nameId?: string;
  _id?: string;
  id?: string;
  name?: string;
  title?: string;
}

interface SsrDetailNavigationContext {
  itemType: SsrDetailItemType;
  item: SsrDetailEntityLike | null | undefined;
  hubName?: string | null;
  frontendSettings?: IFrontendSettings | null;
  source?: 'travelBuddy';
  onPreviewClosed?: () => void;
  routeApiKey?: string;
  previewOwnerDestroyed?: Observable<unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class SsrDetailNavigationService {
  constructor(@Optional() private modalService?: ModalService) {}

  isSsrTripPlannerWidget(): boolean {
    return this.getListingType() === 'trip-planner';
  }

  private canPreviewTripPlannerItem(context: SsrDetailNavigationContext): boolean {
    return !!context.item && (!!context.item.nameId ||
      /^[a-f\d]{24}$/i.test(`${context.item._id || context.item.id || ''}`));
  }

  private shouldNavigateTripPlannerItem(context: SsrDetailNavigationContext): boolean {
    return this.canPreviewTripPlannerItem(context) && this.getScriptValue('ssr-detail') !== 'false' &&
      (context.itemType === 'event' ? context.frontendSettings?.contacts?.eventsSsrEnabled === true
        : context.frontendSettings?.contacts?.placesSsrEnabled === true) && !!this.buildDetailUrl(context);
  }

  private openTripPlannerDetail(context: SsrDetailNavigationContext): boolean {
    if (!this.canPreviewTripPlannerItem(context)) return false;
    if (this.shouldNavigateTripPlannerItem(context)) {
      this.openDetailInNewTab(this.buildDetailUrl(context));
      return true;
    }
    return this.openDetailPreview(context);
  }

  buildDetailPreviewUrl(context: SsrDetailNavigationContext): string {
    const hubName = `${context.hubName || ''}`.trim();
    const itemId = `${context.item?._id || context.item?.id || ''}`.trim();
    const nameId = `${context.item?.nameId || ''}`.trim();
    if (!hubName || (!itemId && !nameId)) return '';

    const apiBase = `${environment.apiAddress || '/api/'}`.replace(/\/+$/, '/');
    const url = new URL(`${apiBase}v2.0/${encodeURIComponent(hubName)}/${context.itemType === 'event' ? 'events' : 'places'}/ssr-detail`, window.location.origin);
    if (itemId) url.searchParams.set('id', itemId);
    else url.searchParams.set('nameId', nameId);
    url.searchParams.set('includeWidget', 'false');
    url.searchParams.set('trailingSlash', `${this.shouldUseTrailingSlash()}`);
    const detailBase = this.getScriptValue(context.itemType === 'event' ? 'event-detail-base-url' : 'place-detail-base-url');
    if (detailBase) url.searchParams.set('detailBaseUrl', detailBase);
    const key = this.resolvePreviewKey(context.routeApiKey);
    if (key) url.searchParams.set('key', key);
    return url.toString();
  }

  openDetailPreview(context: SsrDetailNavigationContext): boolean {
    const url = this.buildDetailPreviewUrl(context);
    if (!this.modalService || !url) return false;

    const preview = this.modalService.show({
      component: SsrDetailPreviewComponent,
      panelClass: 'ig-modal-w-full',
      data: { title: context.item?.name || context.item?.title || 'Detail preview', url },
    });
    if (context.onPreviewClosed) preview.afterClosed().subscribe(context.onPreviewClosed);
    this.closePreviewWithOwner(preview, context.previewOwnerDestroyed);
    return true;
  }

  private closePreviewWithOwner(
    preview: MatDialogRef<SsrDetailPreviewComponent>,
    ownerDestroyed?: Observable<unknown>
  ): void {
    ownerDestroyed?.pipe(take(1), takeUntil(preview.afterClosed())).subscribe(() => preview.close());
  }

  private resolvePreviewKey(routeApiKey?: string): string {
    const locationKey = new URLSearchParams(window.location.search).get('key');
    if (locationKey) return locationKey;
    if (routeApiKey) return routeApiKey;
    for (const attr of ['key', 'api-key', 'apiKey', 'x-api-key']) {
      const value = this.getScriptValue(attr);
      if (value) return value;
    }
    try {
      return new URL(this.getScriptValue('route') || '', window.location.origin).searchParams.get('key') || '';
    } catch (_error) { return ''; }
  }
  private static readonly SSR_DETAIL_NAVIGATION_DELAY_MS = 30;
  private static readonly SSR_DETAIL_LOADING_FALLBACK_MS = 5000;
  private static readonly SSR_DETAIL_ROLLOUT_HUBS = ['randy', 'savannah2', 'testwix'];
  private static readonly TRAVEL_BUDDY_SSR_DETAIL_ROLLOUT_HUBS = [
    'randy',
    'savannah2',
    'testwix',
    'newsmyrnabeach',
    'adventurecoast',
    'panamacityfl'
  ];

  public readonly loadingFallbackMs =
    SsrDetailNavigationService.SSR_DETAIL_LOADING_FALLBACK_MS;

  isSsrListingWidget(itemType: SsrDetailItemType): boolean {
    const expectedListingType = itemType === 'event' ? 'events' : 'places';
    if (this.getListingType() !== expectedListingType) {
      return false;
    }

    const detailBaseAttr =
      itemType === 'event' ? 'event-detail-base-url' : 'place-detail-base-url';
    return !!this.getScriptValue(detailBaseAttr);
  }

  isSsrItineraryListingWidget(itemType: SsrDetailItemType): boolean {
    if (this.getListingType() !== 'itineraries') {
      return false;
    }

    const detailBaseAttr =
      itemType === 'event' ? 'event-detail-base-url' : 'place-detail-base-url';
    return !!this.getScriptValue(detailBaseAttr);
  }

  navigateToDetail(context: SsrDetailNavigationContext): boolean {
    if (this.isSsrTripPlannerWidget()) return this.openTripPlannerDetail(context);
    console.log('🚀 [SsrDetailNavigationService] Navigating to detail...');
    if (this.isRunningInIframe()) {
      console.log('🚀 [SsrDetailNavigationService] Navigating to detail in iframe...');
      if (this.shouldPostMessageDetailUpdate(context)) {
        console.log('🚀 [SsrDetailNavigationService] Navigating to detail in iframe, shouldPostMessageDetailUpdate...');
        this.postDetailUpdateToParent(context);
        return true;
      }

      return false;
    }

    const detailUrl = this.buildDetailUrl(context);
    if (!detailUrl) {
      return false;
    }

    if (context.source === 'travelBuddy' && this.shouldUseConfiguredSsrDetail(context)) {
      this.openDetailInNewTab(detailUrl);
      return true;
    }

    if (this.isSsrItineraryListingWidget(context.itemType)) {
      this.openDetailInNewTab(detailUrl);
      return true;
    }

    if (this.isSsrListingWidget(context.itemType)) {
      this.openDetailInCurrentTab(detailUrl);
      return true;
    }

    if (this.shouldUseConfiguredSsrDetail(context)) {
      this.openDetailInCurrentTab(detailUrl);
      return true;
    }

    return false;
  }

  canNavigateToDetail(context: SsrDetailNavigationContext): boolean {
    if (this.isSsrTripPlannerWidget()) return this.shouldNavigateTripPlannerItem(context);
    if (this.isRunningInIframe()) {
      return this.shouldPostMessageDetailUpdate(context);
    }

    return (
      !!this.buildDetailUrl(context) &&
      (this.isSsrItineraryListingWidget(context.itemType) ||
        this.isSsrListingWidget(context.itemType) ||
        this.shouldUseConfiguredSsrDetail(context))
    );
  }

  shouldUseItinerarySsrIframeFallback(context: SsrDetailNavigationContext): boolean {
    if (!this.isEligibleForItinerarySsrRollout(context)) {
      return false;
    }

    if (context.itemType === 'event') {
      return context.frontendSettings?.contacts?.eventsSsrEnabled !== true;
    }

    return context.frontendSettings?.contacts?.placesSsrEnabled !== true;
  }

  shouldUseTravelBuddySsrIframeFallback(context: SsrDetailNavigationContext): boolean {
    if (!this.isEligibleForTravelBuddySsrDetailRollout(context)) {
      return false;
    }

    if (context.itemType === 'event') {
      return context.frontendSettings?.contacts?.eventsSsrEnabled !== true;
    }

    return context.frontendSettings?.contacts?.placesSsrEnabled !== true;
  }

  navigateToDetailInCurrentTab(context: SsrDetailNavigationContext): boolean {
    if (!this.isSsrListingWidget(context.itemType)) {
      return false;
    }

    const detailUrl = this.buildDetailUrl(context);
    if (!detailUrl) {
      return false;
    }

    this.openDetailInCurrentTab(detailUrl);

    return true;
  }

  buildDetailUrl(context: SsrDetailNavigationContext): string {
    const itemKey = `${context.item?.nameId || context.item?._id || context.item?.id || ''}`.trim();
    if (!itemKey) {
      return '';
    }

    const hubName = `${context.hubName || ''}`.trim();
    const frontendSettings = context.frontendSettings;
    const detailBaseAttr =
      context.itemType === 'event' ? 'event-detail-base-url' : 'place-detail-base-url';
    const fallbackBase =
      context.itemType === 'event'
        ? frontendSettings?.contacts?.eventCalendarURL ||
          this.getScriptValue('event-share-base-url') ||
          (hubName ? `/${hubName}/events` : '')
        : frontendSettings?.contacts?.placeCalendarURL || (hubName ? `/${hubName}/places` : '');
    const detailBaseUrl = this.getScriptValue(detailBaseAttr) || fallbackBase;

    return this.buildPathStyleDetailUrl(
      detailBaseUrl,
      itemKey,
      context.itemType === 'event' ? 'e' : 'p'
    );
  }

  private buildPathStyleDetailUrl(
    baseUrl: string,
    itemKey: string,
    leadingSegment: string
  ): string {
    const rawBaseUrl = `${baseUrl || ''}`.trim();
    const rawItemKey = `${itemKey || ''}`.trim();
    if (!rawBaseUrl || !rawItemKey) {
      return '';
    }

    try {
      const hasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(rawBaseUrl);
      const isProtocolRelative = /^\/\//.test(rawBaseUrl);
      const url = new URL(rawBaseUrl, window.location.origin);
      const segments = (url.pathname || '/').split('/').filter(Boolean);
      const encodedLeadingSegment = leadingSegment ? encodeURIComponent(leadingSegment) : '';

      if (
        encodedLeadingSegment &&
        segments[segments.length - 1] !== encodedLeadingSegment
      ) {
        segments.push(encodedLeadingSegment);
      }

      segments.push(encodeURIComponent(rawItemKey));
      const pathname = `/${segments.join('/')}${this.shouldUseTrailingSlash() ? '/' : ''}`;
      const result = `${pathname}${url.search || ''}${url.hash || ''}`;

      if (hasProtocol) {
        return `${url.origin}${result}`;
      }

      if (isProtocolRelative) {
        return `//${url.host}${result}`;
      }

      return rawBaseUrl.startsWith('/') ? result : result.replace(/^\//, '');
    } catch (_error) {
      let normalizedBaseUrl = rawBaseUrl.replace(/\/+$/, '');
      const encodedLeadingSegment = leadingSegment ? encodeURIComponent(leadingSegment) : '';

      if (
        encodedLeadingSegment &&
        !normalizedBaseUrl.endsWith(`/${encodedLeadingSegment}`)
      ) {
        normalizedBaseUrl = `${normalizedBaseUrl}/${encodedLeadingSegment}`;
      }

      return `${normalizedBaseUrl}/${encodeURIComponent(rawItemKey)}${
        this.shouldUseTrailingSlash() ? '/' : ''
      }`;
    }
  }

  private shouldUseTrailingSlash(): boolean {
    return `${this.getScriptValue('trailing-slash') || ''}`.trim().toLowerCase() === 'true';
  }

  private getScriptValue(valueName: string): string {
    const wrapper = this.getWrapper();
    return (
      wrapper?.attributes[valueName] ||
      wrapper?.attributes[`data-${valueName}`]
    )?.value;
  }

  private getWrapper(): HTMLElement | null {
    return (
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix')
    );
  }

  private getListingType(): string {
    return this.getWrapper()?.getAttribute('data-imgoing-ssr-listing') || '';
  }

  private shouldUseConfiguredSsrDetail(
    context: SsrDetailNavigationContext
  ): boolean {
    if (!this.isEligibleForSsrDetailRollout(context)) {
      return false;
    }

    if (context.itemType === 'event') {
      return context.frontendSettings?.contacts?.eventsSsrEnabled === true;
    }

    return context.frontendSettings?.contacts?.placesSsrEnabled === true;
  }

  private shouldPostMessageDetailUpdate(
    context: SsrDetailNavigationContext
  ): boolean {
    return this.shouldUseConfiguredSsrDetail(context);
  }

  private isEligibleForItinerarySsrRollout(
    context: SsrDetailNavigationContext
  ): boolean {
    return (
      this.isSsrItineraryListingWidget(context.itemType) &&
      this.isEligibleForSsrDetailRollout(context)
    );
  }

  private isEligibleForSsrDetailRollout(
    context: SsrDetailNavigationContext
  ): boolean {
    if (context.source === 'travelBuddy') {
      return this.isEligibleForTravelBuddySsrDetailRollout(context);
    }

    const normalizedHubName = `${context.hubName || ''}`.trim().toLowerCase();
    return SsrDetailNavigationService.SSR_DETAIL_ROLLOUT_HUBS.includes(normalizedHubName);
  }

  private isEligibleForTravelBuddySsrDetailRollout(
    context: SsrDetailNavigationContext
  ): boolean {
    const normalizedHubName = `${context.hubName || ''}`.trim().toLowerCase();
    return SsrDetailNavigationService.TRAVEL_BUDDY_SSR_DETAIL_ROLLOUT_HUBS.includes(
      normalizedHubName
    );
  }

  private isRunningInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (_error) {
      return true;
    }
  }

  private postDetailUpdateToParent(context: SsrDetailNavigationContext): void {
    const paramValue = `${context.item?.nameId || context.item?._id || context.item?.id || ''}`.trim();
    if (!paramValue) {
      return;
    }

    window.setTimeout(() => {
      const payload = {
          type: 'imgoing-url-update',
          paramName: context.itemType === 'event' ? 'imgoing-event' : 'imgoing-place',
          paramValue,
        };
      window.parent.postMessage(
        payload,
        '*'
      );
      console.log('🚀 [SsrDetailNavigationService] Message posted...', payload);
    }, SsrDetailNavigationService.SSR_DETAIL_NAVIGATION_DELAY_MS);
  }

  private openDetailInCurrentTab(detailUrl: string): void {
    window.setTimeout(() => {
      window.location.assign(detailUrl);
    }, SsrDetailNavigationService.SSR_DETAIL_NAVIGATION_DELAY_MS);
  }

  private openDetailInNewTab(detailUrl: string): void {
    window.setTimeout(() => {
      const anchor = document.createElement('a');
      anchor.href = detailUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }, SsrDetailNavigationService.SSR_DETAIL_NAVIGATION_DELAY_MS);
  }
}
